import styled from "styled-components"

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100svh;
`

export const Main = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 0 64px 0;
`

export const Block = styled.div<{ $k: number }>`
  max-width: 560px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  box-shadow: 0 2px 4px 4px rgba(0, 0, 0, 0.1);
  padding: 24px;
  border-radius: 12px;

  opacity: 0;
  ${({ $k, theme }) =>
    theme.animations.types.fade +
    theme.animations.durations.main +
    theme.animations.delays.main($k)}

  h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #111827;
  }

  p {
    margin: 0;
    font-size: 15px;
    color: #4b5563;
    line-height: 1.45;
  }
`

export const Button = styled.button`
  width: 100%;
  min-height: 48px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  outline: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white.main};
  background-color: ${({ theme }) => theme.colors.blue.main};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.blue.main};
    outline-offset: 2px;
  }
`

export const LinkButton = styled.button`
  background: none;
  border: none;
  outline: none;
  padding: 8px 4px;
  min-height: 44px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.blue.main};

  &:hover {
    text-decoration: underline;
  }
`

/* Visualização dos ingressos */

export const View = styled.div<{ $k: number }>`
  max-width: 560px;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  opacity: 0;
  ${({ $k, theme }) =>
    theme.animations.types.fade +
    theme.animations.durations.main +
    theme.animations.delays.main($k)}
`

export const ViewHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  h1 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 28px;
    font-weight: 800;
    color: #111827;
    line-height: 1.15;

    svg {
      flex: 0 0 auto;
      width: 36px;
      height: 36px;
      color: ${({ theme }) => theme.colors.blue.main};
    }
  }

  strong {
    padding-left: 48px;
    font-size: 20px;
    font-weight: 600;
    color: #6b7280;
  }
`

export const Actions = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 8px;
`

export const ActionButton = styled.button<{ $primary?: boolean }>`
  width: 100%;
  min-height: 56px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  border-radius: 12px;
  outline: none;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.blue.main};
  background-color: ${({ $primary, theme }) =>
    $primary ? theme.colors.blue.main : "#fff"};
  color: ${({ $primary, theme }) =>
    $primary ? "#fff" : theme.colors.blue.main};

  svg {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
  }

  & > span {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    font-size: 15px;
    font-weight: 700;
  }

  small {
    font-size: 12px;
    opacity: ${({ $primary }) => ($primary ? 0.85 : 0.75)};
    color: ${({ $primary }) => ($primary ? "#fff" : "#4b5563")};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.blue.main};
    outline-offset: 2px;
  }
`

export const ActionError = styled.p`
  grid-column: 1 / -1;
  margin: 0;
  padding: 10px 14px;
  border-radius: 8px;
  background-color: #fbd61840;
  font-size: 14px;
  color: #374151;
`

/* Carrossel horizontal com foco automático (snap) no ingresso central. */
export const Carousel = styled.ul`
  /* Sempre sobra uma "fatia" (>= 32px) do ingresso vizinho à vista. */
  --gap: 12px;
  --peek: 32px;

  list-style: none;
  margin: 0;
  padding: 8px max(calc(var(--peek) + var(--gap)), calc((100% - 300px) / 2))
    16px;
  display: flex;
  gap: var(--gap);
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

export const Slide = styled.li<{ $active: boolean }>`
  flex: 0 0 100%;
  min-width: 0;
  scroll-snap-align: center;
  scroll-snap-stop: always;
  opacity: ${({ $active }) => ($active ? 1 : 0.7)};
  transform: scale(${({ $active }) => ($active ? 1 : 0.94)});
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/* Cartão em formato de ingresso: recortes laterais na linha tracejada. */
const notch = (y: "0" | "100%") => `
  -webkit-mask:
    radial-gradient(circle 12px at 0 ${y}, #0000 97%, #000) left / 51% 100% no-repeat,
    radial-gradient(circle 12px at 100% ${y}, #0000 97%, #000) right / 51% 100% no-repeat;
  mask:
    radial-gradient(circle 12px at 0 ${y}, #0000 97%, #000) left / 51% 100% no-repeat,
    radial-gradient(circle 12px at 100% ${y}, #0000 97%, #000) right / 51% 100% no-repeat;
`

const corners = (color: string) => `
  linear-gradient(${color}, ${color}) 0 0 / 22px 3px no-repeat,
  linear-gradient(${color}, ${color}) 0 0 / 3px 22px no-repeat,
  linear-gradient(${color}, ${color}) 100% 0 / 22px 3px no-repeat,
  linear-gradient(${color}, ${color}) 100% 0 / 3px 22px no-repeat,
  linear-gradient(${color}, ${color}) 0 100% / 22px 3px no-repeat,
  linear-gradient(${color}, ${color}) 0 100% / 3px 22px no-repeat,
  linear-gradient(${color}, ${color}) 100% 100% / 22px 3px no-repeat,
  linear-gradient(${color}, ${color}) 100% 100% / 3px 22px no-repeat
`

export const TicketCard = styled.div`
  width: 100%;
  filter: drop-shadow(0 4px 14px rgba(17, 24, 39, 0.16));

  .top,
  .bottom {
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .top {
    gap: 8px;
    padding: 16px 16px 22px;
    border-radius: 20px 20px 0 0;
    ${notch("100%")}

    .head {
      position: relative;
      width: 100%;
      min-height: 18px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .index {
      font-size: 14px;
      font-weight: 500;
      color: #374151;
    }

    .dots {
      position: absolute;
      right: 0;
      display: flex;
      gap: 5px;

      i {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background-color: #d1d5db;
      }

      i.on {
        background-color: ${({ theme }) => theme.colors.blue.main};
      }
    }

    strong {
      display: block;
      font-size: 24px;
      font-weight: 800;
      color: #111827;
      overflow-wrap: anywhere;
    }

    .batch {
      padding: 3px 12px;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.blue.main};
      background-color: rgba(0, 97, 230, 0.1);
    }
  }

  .bottom {
    position: relative;
    padding: 20px 16px 0;
    border-radius: 0 0 20px 20px;
    overflow: hidden;
    ${notch("0")}

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 18px;
      right: 18px;
      border-top: 2px dashed #d1d5db;
    }

    .qr {
      padding: 14px;
      background: ${({ theme }) => corners(theme.colors.blue.main)};
      display: grid;
      place-items: center;

      svg {
        width: min(44vw, 190px);
        height: min(44vw, 190px);
      }
    }

    .code {
      max-width: 100%;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 2px 6px;
      border: none;
      border-radius: 6px;
      outline: none;
      cursor: pointer;
      margin-top: 8px;
      font-size: 14px;
      color: #374151;
      background: none;
      overflow-wrap: anywhere;

      svg {
        flex: 0 0 auto;
        width: 14px;
        height: 14px;
      }

      &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.blue.main};
      }
    }

    .hint {
      width: calc(100% + 32px);
      margin-top: 16px;
      padding: 10px 12px;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: #4b5563;
      background-color: #f3f4f6;

      svg {
        flex: 0 0 auto;
        width: 16px;
        height: 16px;
      }
    }
  }
`
